import { useState, useRef, useEffect, useCallback, useMemo } from "react";

interface AppEntry {
  slug: string;
  name: string;
  description: string;
  stars: number;
  timeAgo: string;
  thumbUrl: string;
  appUrl: string;
}

// ── Helpers ────────────────────────────────────────────────────────

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function hashSlug(slug: string): number {
  let h = 0;
  for (let i = 0; i < slug.length; i++) {
    h = ((h << 5) - h + slug.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 8;
}

function parseAppsFromHtml(html: string): AppEntry[] {
  const slugRegex = /href="\/apps\/([^/]+)\/go"/g;
  let m: RegExpExecArray | null;
  const seen = new Set<string>();
  const slugs: string[] = [];
  while ((m = slugRegex.exec(html)) !== null) {
    if (!seen.has(m[1])) {
      seen.add(m[1]);
      slugs.push(m[1]);
    }
  }

  const apps: AppEntry[] = [];
  for (const slug of slugs) {
    const idx = html.indexOf("/apps/" + slug + "/go");
    if (idx < 0) continue;
    const ctx = html.substring(idx, idx + 3000);

    const nameMatch = ctx.match(
      /tracking-tight text-white hover:underline">([^<]+)/
    );
    const descMatch = ctx.match(
      /mt-1 truncate font-mono text-\[11px\] text-white\/50">([^<]+)/
    );
    const starsMatch = ctx.match(
      /mt-2 font-mono text-\[10px\] uppercase tracking-\[0\.2em\] text-white\/35">(\d+)/
    );
    const timeMatch = ctx.match(
      /shrink-0 font-mono text-\[9px\] uppercase tracking-\[0\.22em\] text-white\/35">([^<]+)/
    );

    apps.push({
      slug,
      name: nameMatch ? nameMatch[1] : slug,
      description: descMatch
        ? descMatch[1].replace(/&quot;/g, '"').replace(/&amp;/g, "&")
        : "",
      stars: starsMatch ? parseInt(starsMatch[1]) : 0,
      timeAgo: timeMatch ? timeMatch[1] : "",
      thumbUrl: `https://${slug}.gitlawb.app/_thumb.png`,
      appUrl: `https://playground.gitlawb.com/apps/${slug}/go`,
    });
  }
  return apps;
}

// ── Fetch: show first page immediately, load rest in background ────

async function fetchFirstPage(): Promise<AppEntry[]> {
  const html = await fetch("https://super-mud-9fe1.mitjet.workers.dev/apps?page=1").then((r) => r.text());
  return parseAppsFromHtml(html);
}

async function fetchRemainingApps(
  onBatch: (apps: AppEntry[]) => void
): Promise<void> {
  let page = 2;
  let hasMore = true;
  const batchSize = 8;

  while (hasMore) {
    const pages: number[] = [];
    for (let i = 0; i < batchSize; i++) {
      pages.push(page + i);
    }

    const results = await Promise.all(
      pages.map((p) =>
        fetch(`https://super-mud-9fe1.mitjet.workers.dev/apps?page=${p}`)
          .then((r) => (r.ok ? r.text() : ""))
          .catch(() => "")
      )
    );

    let anyResults = false;
    for (const html of results) {
      if (!html) continue;
      const apps = parseAppsFromHtml(html);
      if (apps.length > 0) {
        onBatch(apps);
        anyResults = true;
      }
    }

    if (!anyResults) {
      hasMore = false;
    } else {
      page += batchSize;
      if (page > 200) hasMore = false;
    }
  }
}

// ── IntersectionObserver hook ──────────────────────────────────────

function useIsVisible(ref: React.RefObject<HTMLElement | null>, rootMargin = "1000px") {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isVisible;
}

// ── Constants ──────────────────────────────────────────────────────

const NEARBY_RANGE = 1;

// ── Single Slide ───────────────────────────────────────────────────

function AppSlide({
  app,
  index,
  currentIndex,
  isInteracting,
  onEnterInteract,
}: {
  app: AppEntry;
  index: number;
  currentIndex: number;
  isInteracting: boolean;
  onEnterInteract: () => void;
}) {
  const slideRef = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(slideRef, "500px");
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);
  const isNearby = Math.abs(index - currentIndex) <= NEARBY_RANGE;
  const isActive = index === currentIndex;

  useEffect(() => {
    if (!isNearby) {
      setIframeLoaded(false);
      setThumbFailed(false);
    }
  }, [isNearby]);

  const gradClass = `grad-${hashSlug(app.slug)}`;
  const firstLetter = app.name.charAt(0).toUpperCase();

  return (
    <div ref={slideRef} className="app-slide">
      {/* Only render content when near viewport */}
      {isVisible ? (
        <div className="slide-preview">
          {isNearby ? (
            <>
              {/* Thumbnail while iframe loads */}
              {!iframeLoaded && !thumbFailed && (
                <img
                  src={app.thumbUrl}
                  alt=""
                  className="slide-thumb"
                  onError={() => setThumbFailed(true)}
                />
              )}
              {/* Gradient fallback if thumb failed */}
              {!iframeLoaded && thumbFailed && (
                <div className={`slide-placeholder ${gradClass}`}>
                  <div className="slide-placeholder-letter">{firstLetter}</div>
                </div>
              )}
              <iframe
                src={`https://${app.slug}.gitlawb.app`}
                title={app.name}
                loading="lazy"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                onLoad={() => setIframeLoaded(true)}
                style={{
                  width: "100%",
                  height: "100%",
                  border: 0,
                  opacity: iframeLoaded ? 1 : 0,
                  transition: "opacity 0.3s",
                }}
              />
            </>
          ) : (
            <div className={`slide-placeholder ${gradClass}`}>
              <div className="slide-placeholder-letter">{firstLetter}</div>
              <span className="slide-placeholder-name">{app.name}</span>
            </div>
          )}

          {/* Tap-to-interact prompt (only on active slide, when not already interacting) */}
          {isActive && !isInteracting && isNearby && (
            <div
              className={`tap-overlay${isActive && !isInteracting ? " visible" : ""}`}
              onClick={onEnterInteract}
            >
              <div className="tap-overlay-inner">
                <span className="tap-overlay-icon">&#9729;</span>
                <span className="tap-overlay-text">tap to interact</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={`slide-placeholder ${gradClass}`} />
      )}

      {/* Right-side action buttons */}
      <div className={`slide-actions${isInteracting ? " hidden" : ""}`}>
        <a
          href={app.appUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="action-btn"
        >
          <div className="action-icon">&#x2197;</div>
          <span className="action-label">Open</span>
        </a>
        <div className="action-btn" style={{ cursor: "default" }}>
          <div className="action-icon">&#x2605;</div>
          <span className="action-label">{app.stars}</span>
        </div>
      </div>

      {/* Bottom info bar */}
      <div className={`slide-info${isInteracting ? " hidden" : ""}`}>
        <div className="slide-name">@{app.name}</div>
        {app.description && (
          <div className="slide-description">{app.description}</div>
        )}
        <div className="slide-meta">
          <span className="slide-meta-item">
            <span>&#x2605;</span> {app.stars}
          </span>
          <span className="slide-meta-item">{app.timeAgo}</span>
        </div>
      </div>
    </div>
  );
}

// ── Main App ───────────────────────────────────────────────────────

export default function App() {
  const [apps, setApps] = useState<AppEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [bgLoading, setBgLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [interacting, setInteracting] = useState(false);
  const [showKbdHints, setShowKbdHints] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const kbdTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // 1. Show first page immediately, load rest in background
  useEffect(() => {
    fetchFirstPage().then((firstPage) => {
      setApps(shuffleArray(firstPage));
      setLoading(false);
      setBgLoading(true);

      fetchRemainingApps((batch) => {
        setApps((prev) => {
          const existingSlugs = new Set(prev.map((a) => a.slug));
          const newApps = batch.filter((a) => !existingSlugs.has(a.slug));
          if (newApps.length === 0) return prev;
          return [...prev, ...newApps];
        });
      }).then(() => {
        setBgLoading(false);
      });
    });
  }, []);

  // 2. Re-fetch page 1 when tab becomes visible (auto-update)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;

      fetchFirstPage().then((freshApps) => {
        if (freshApps.length === 0) return;
        setApps((prev) => {
          const existingSlugs = new Set(prev.map((a) => a.slug));
          const newApps = freshApps.filter((a) => !existingSlugs.has(a.slug));
          if (newApps.length === 0) return prev;
          return [...prev, ...newApps];
        });
      });
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  // 3. Track scroll → current index
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const slideHeight = window.innerHeight;
    if (slideHeight === 0) return;
    const newIndex = Math.round(container.scrollTop / slideHeight);
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < apps.length) {
      setCurrentIndex(newIndex);
      // Auto-exit interact mode when scrolling to next slide
      setInteracting(false);
    }
  }, [currentIndex, apps.length]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // 3. Keyboard navigation + hints
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Show keyboard hints on ? press
      if (e.key === "?" || (e.key === "/" && e.shiftKey)) {
        e.preventDefault();
        setShowKbdHints((v) => !v);
        return;
      }

      if (interacting) {
        // Escape exits interact mode
        if (e.key === "Escape") {
          e.preventDefault();
          setInteracting(false);
        }
        return;
      }

      const container = containerRef.current;
      if (!container) return;
      const slideHeight = window.innerHeight;

      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        container.scrollTo({
          top: Math.min(
            (currentIndex + 1) * slideHeight,
            (apps.length - 1) * slideHeight
          ),
          behavior: "smooth",
        });
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        container.scrollTo({
          top: Math.max((currentIndex - 1) * slideHeight, 0),
          behavior: "smooth",
        });
      } else if (e.key === "r") {
        // Random jump
        e.preventDefault();
        handleRandomJump();
      } else if (e.key === "Home") {
        e.preventDefault();
        container.scrollTo({ top: 0, behavior: "smooth" });
      } else if (e.key === "i") {
        e.preventDefault();
        setInteracting(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, apps.length, interacting]);

  // Auto-hide keyboard hints after 4s
  useEffect(() => {
    if (showKbdHints) {
      kbdTimerRef.current = setTimeout(() => setShowKbdHints(false), 4000);
      return () => clearTimeout(kbdTimerRef.current);
    }
  }, [showKbdHints]);

  // 4. Random jump
  const handleRandomJump = useCallback(() => {
    if (apps.length <= 1) return;
    const container = containerRef.current;
    if (!container) return;
    const slideHeight = window.innerHeight;
    let newIndex: number;
    do {
      newIndex = Math.floor(Math.random() * apps.length);
    } while (newIndex === currentIndex && apps.length > 1);
    container.scrollTo({ top: newIndex * slideHeight, behavior: "smooth" });
  }, [apps.length, currentIndex]);

  // 5. Scroll to top
  const handleScrollToTop = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // 6. Enter interact mode
  const handleEnterInteract = useCallback(() => {
    setInteracting(true);
  }, []);

  // ── Loading screen ─────────────────────────────────────────────

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <div className="loading-text">Loading apps...</div>
        <div className="loading-bar-track">
          <div className="loading-bar-fill" style={{ width: "10%" }} />
        </div>
      </div>
    );
  }

  if (apps.length === 0) {
    return (
      <div className="loading-screen">
        <div className="loading-text">No apps found</div>
      </div>
    );
  }

  return (
    <>
      {/* Fixed header */}
      <div className="app-header">
        <div className="header-left">
          <div className="logo" onClick={handleScrollToTop} title="Scroll to top">
            <svg className="logo-icon" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="0.5" y="0.5" width="27" height="27" rx="7" fill="#fff" fillOpacity="0.08" stroke="#fff" strokeOpacity="0.15" />
              <path d="M11 8 L21 14 L11 20Z" fill="#fff" />
            </svg>
            <span className="logo-text">GitGok</span>
          </div>
        </div>
        <div className="header-right">
          <button className="header-btn" onClick={handleRandomJump} title="Random app (R)">
            &#x2195;
          </button>
          <span className="header-counter">
            {currentIndex + 1} / {apps.length}
          </span>
        </div>
      </div>

      {/* Background loading indicator */}
      {bgLoading && (
        <div className="bg-loading">
          <span className="bg-loading-dot" />
          loading {loadProgress} more apps...
        </div>
      )}

      {/* Scrollable container */}
      <div
        ref={containerRef}
        className={`app-container${interacting ? " interacting" : ""}`}
      >
        {apps.map((app, i) => (
          <AppSlide
            key={app.slug}
            app={app}
            index={i}
            currentIndex={currentIndex}
            isInteracting={interacting}
            onEnterInteract={handleEnterInteract}
          />
        ))}
      </div>

      {/* Scroll hint on first slide */}
      {currentIndex === 0 && !interacting && (
        <div className="scroll-hint">
          <div className="scroll-hint-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <span className="scroll-hint-text">scroll</span>
        </div>
      )}

      {/* Keyboard hints overlay */}
      <div className={`kbd-hints${showKbdHints ? " visible" : ""}`}>
        <div className="kbd-row">
          <span className="kbd">j</span>/<span className="kbd">&#x2193;</span> next
        </div>
        <div className="kbd-row">
          <span className="kbd">k</span>/<span className="kbd">&#x2191;</span> prev
        </div>
        <div className="kbd-row">
          <span className="kbd">r</span> random
        </div>
        <div className="kbd-row">
          <span className="kbd">i</span> interact
        </div>
        <div className="kbd-row">
          <span className="kbd">esc</span> scroll mode
        </div>
        <div className="kbd-row">
          <span className="kbd">?</span> toggle this
        </div>
      </div>

      {/* Interact mode toggle */}
      <button
        className={`interact-toggle${interacting ? " active" : ""}`}
        onClick={() => setInteracting(!interacting)}
        title={interacting ? "Switch to scroll mode (Esc)" : "Switch to interact mode (I)"}
      >
        <span className="interact-dot" />
        {interacting ? "← scroll" : "interact"}
      </button>
    </>
  );
}
